import React, { useEffect, useState } from 'react';
import { Container, Typography, Grid, Paper, Box, Button, Alert, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import api from '../axios';
import ModuleHeader from '../components/ModuleHeader';
import { useNavigate } from 'react-router-dom';

const TEAL = '#00F1A7';
const LIME = '#B4F300';

function CalendarioAulas() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [aulas, setAulas] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [userTipo, setUserTipo] = useState(null);
  const [userEMonitor, setUserEMonitor] = useState(false);
  const [professorId, setProfessorId] = useState(null);

  // Detectar tipo de usuário e se é monitor
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserTipo(payload.tipo);
        
        // Se for professor, buscar dados para ver se é monitor
        if (payload.tipo === 'professor') {
          api.get('/professor/perfil')
            .then(response => {
              setUserEMonitor(response.data.e_monitor || false);
              setProfessorId(response.data.id); // Guardar ID do professor
            })
            .catch(err => {
              console.error('Erro ao buscar perfil do professor:', err);
            });
        }
      } catch (e) {
        console.error('Erro ao parsear token', e);
      }
    }
  }, []);

  // Carregar aulas do mês atual
  useEffect(() => {
    fetchAulas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  // Recarregar perfil do professor quando componente ganha foco
  useEffect(() => {
    const reloadProfile = () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.tipo === 'professor') {
            api.get('/professor/perfil')
              .then(response => {
                setUserEMonitor(response.data.e_monitor || false);
                setProfessorId(response.data.id);
              })
              .catch(err => {
                console.error('Erro ao recarregar perfil:', err);
              });
          }
        } catch (e) {
          console.error('Erro ao parsear token', e);
        }
      }
    };

    // Recarregar ao ganhar foco
    window.addEventListener('focus', reloadProfile);
    return () => window.removeEventListener('focus', reloadProfile);
  }, []);

  const fetchAulas = async () => {
    setError('');
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const startDate = new Date(year, month, 1);
      const endDate = new Date(year, month + 1, 0);

      const startStr = startDate.toISOString().split('T')[0];
      const endStr = endDate.toISOString().split('T')[0];

      const response = await api.get(`/aulas/?start=${startStr}&end=${endStr}`);
      setAulas(response.data);
    } catch (err) {
      setError('Erro ao carregar aulas');
      console.error(err);
    }
  };

  const handleReservar = async (aulaId) => {
    setMessage('');
    setError('');
    try {
      await api.post(`/aulas/${aulaId}/reservar`);
      setMessage('Aula reservada com sucesso!');
      fetchAulas(); // Recarregar
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erro ao reservar aula';
      setError(errorMsg);
    }
  };

  const handleDesreservar = async (aulaId) => {
    setMessage('');
    setError('');
    try {
      await api.delete(`/aulas/${aulaId}/reservar`);
      setMessage('Reserva removida com sucesso!');
      fetchAulas(); // Recarregar
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erro ao desreservar aula';
      setError(errorMsg);
    }
  };

  const handleReservarMonitor = async (aulaId) => {
    setMessage('');
    setError('');
    try {
      await api.post(`/aulas/${aulaId}/reservar-monitor`);
      setMessage('Inscrito como monitor com sucesso!');
      fetchAulas(); // Recarregar
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erro ao reservar como monitor';
      setError(errorMsg);
    }
  };

  const handleDesreservarMonitor = async (aulaId) => {
    setMessage('');
    setError('');
    try {
      await api.delete(`/aulas/${aulaId}/reservar-monitor`);
      setMessage('Inscrição de monitor removida com sucesso!');
      fetchAulas(); // Recarregar
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erro ao remover inscrição de monitor';
      setError(errorMsg);
    }
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const days = [];
    // Dias vazios antes do primeiro dia
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }
    // Dias do mês
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const getAulasPorDia = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    // Compara apenas a parte da data (aula.data é ISO string completa)
    return aulas.filter(aula => aula.data && aula.data.startsWith(dateStr));
  };

  const getCorStatus = (aula) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const aulaDate = new Date(aula.data);
    aulaDate.setHours(0, 0, 0, 0);

    // Passada = cinza
    if (aulaDate < hoje) return '#999';
    
    const temProfessor = aula.professor_id != null;
    const temMonitor = aula.monitor_id != null;
    
    // Sistema de 4 cores:
    if (temProfessor && temMonitor) return '#1b5e20'; // Verde escuro - Completa
    if (temProfessor) return '#ff9800'; // Laranja - Só professor
    if (temMonitor) return '#2196f3'; // Azul - Só monitor
    return '#4caf50'; // Verde - Disponível
  };

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const days = getDaysInMonth();

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>
      <ModuleHeader module="Start" title="Calendário de Aulas" />

      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Navegação de mês */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          mb: 3,
          p: 2,
          backgroundColor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1
        }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={handlePrevMonth}
            variant="contained"
            color="primary"
            fullWidth
            sx={{ minWidth: 120, fontWeight: 600 }}
          >
            ANTERIOR
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: LIME }}>
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </Typography>
          <Button 
            endIcon={<ArrowForwardIcon />} 
            onClick={handleNextMonth}
            variant="contained"
            color="primary"
            fullWidth
            sx={{ minWidth: 120, fontWeight: 600 }}
          >
            PRÓXIMO
          </Button>
        </Box>

        {/* Legenda de cores */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 3, 
          flexWrap: 'wrap',
          p: 2,
          backgroundColor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Chip
            label="Disponível"
            sx={{ backgroundColor: '#4caf50', color: '#fff', fontWeight: 'bold', fontSize: '0.875rem' }}
            size="medium"
          />
          <Chip
            label="Com Professor (precisa monitor)"
            sx={{ backgroundColor: '#ff9800', color: '#fff', fontWeight: 'bold', fontSize: '0.875rem' }}
            size="medium"
          />
          <Chip
            label="Com Monitor (precisa professor)"
            sx={{ backgroundColor: '#2196f3', color: '#fff', fontWeight: 'bold', fontSize: '0.875rem' }}
            size="medium"
          />
          <Chip
            label="Completa (professor + monitor)"
            sx={{ backgroundColor: '#1b5e20', color: '#fff', fontWeight: 'bold', fontSize: '0.875rem' }}
            size="medium"
          />
          <Chip
            label="Passada/Indisponível"
            sx={{ backgroundColor: '#999', color: '#fff', fontWeight: 'bold', fontSize: '0.875rem' }}
            size="medium"
          />
        </Box>

        {/* Cabeçalho da semana */}
        <Grid container spacing={1} sx={{ mb: 1 }}>
          {weekDays.map((day, idx) => (
            <Grid item xs={12} sm={12/7} key={idx} sx={{ textAlign: 'center' }}>
              <Box sx={{ 
                backgroundColor: '#1976d2', 
                color: '#fff', 
                py: 1, 
                borderRadius: 1,
                fontWeight: 'bold'
              }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {day}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Dias do mês */}
        <Grid container spacing={1} sx={{ mt: 1 }}>
          {days.map((date, idx) => {
            const aulasNoDia = getAulasPorDia(date);
            return (
              <Grid item xs={12} sm={12/7} key={idx} sx={{ minHeight: { xs: 60, sm: 100, md: 140 }, display: 'flex' }}>
                {date ? (
                  <Paper
                    sx={{
                      p: { xs: 0.5, sm: 1.5 },
                      minHeight: { xs: 60, sm: 100, md: 140 },
                      width: '100%',
                      border: '2px solid rgba(0,241,167,0.15)',
                      backgroundColor: '#0A141C',
                      borderRadius: 2,
                      transition: 'box-shadow 0.3s',
                      '&:hover': {
                        boxShadow: 3,
                      },
                    }}
                  >
                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 1, color: TEAL }}>
                      {date.getDate()}
                    </Typography>
                    {aulasNoDia.map((aula) => {
                      const cor = getCorStatus(aula);
                      const aulaDate = new Date(aula.data);
                      const hoje = new Date();
                      hoje.setHours(0, 0, 0, 0);
                      aulaDate.setHours(0, 0, 0, 0);
                      const aulaFutura = aulaDate >= hoje;
                      
                      // Se perfil de monitor está ativo, PRIORIZAR ações de monitor
                      let podeReservar, podeLiberar, podeReservarMonitor, podeLiberarMonitor;
                      
                      if (userEMonitor) {
                        // MODO MONITOR: prioriza ações de monitor
                        podeReservarMonitor = !aula.monitor_id && aulaFutura && aula.professor_id !== professorId;
                        podeLiberarMonitor = aula.monitor_id === professorId;
                        // Não permite ações de professor quando em modo monitor
                        podeReservar = false;
                        podeLiberar = false;
                      } else {
                        // MODO PROFESSOR: apenas ações de professor
                        podeReservar = userTipo === 'professor' && !aula.professor_id && aulaFutura;
                        podeLiberar = userTipo === 'professor' && aula.professor_id === professorId;
                        podeReservarMonitor = false;
                        podeLiberarMonitor = false;
                      }
                      
                      // Admin pode tudo
                      if (userTipo === 'admin' || userTipo === 'admin_tenant') {
                        podeReservar = !aula.professor_id && aulaFutura;
                        podeLiberar = aula.professor_id != null;
                        podeReservarMonitor = !aula.monitor_id && aulaFutura;
                        podeLiberarMonitor = aula.monitor_id != null;
                      }
                      
                      // Determina se o card é clicável
                      const isClickable = podeReservar || podeLiberar || podeReservarMonitor || podeLiberarMonitor;
                      
                      // Função de clique no card (monitor tem prioridade se perfil ativo)
                      const handleCardClick = () => {
                        if (userEMonitor) {
                          // Modo monitor: priorizar ações de monitor
                          if (podeLiberarMonitor) handleDesreservarMonitor(aula.id);
                          else if (podeReservarMonitor) handleReservarMonitor(aula.id);
                        } else {
                          // Modo professor normal
                          if (podeLiberar) handleDesreservar(aula.id);
                          else if (podeReservar) handleReservar(aula.id);
                        }
                        
                        // Admin pode fazer ambos
                        if (userTipo === 'admin' || userTipo === 'admin_tenant') {
                          if (podeReservarMonitor) handleReservarMonitor(aula.id);
                          else if (podeLiberarMonitor) handleDesreservarMonitor(aula.id);
                          else if (podeReservar) handleReservar(aula.id);
                          else if (podeLiberar) handleDesreservar(aula.id);
                        }
                      };
                      
                      // Extrair primeiro nome
                      const getPrimeiroNome = (nomeCompleto) => {
                        if (!nomeCompleto) return '';
                        return nomeCompleto.split(' ')[0];
                      };

                      return (
                        <Box
                          key={aula.id}
                          onClick={isClickable ? handleCardClick : undefined}
                          sx={{
                            backgroundColor: cor,
                            color: '#fff',
                            p: 1.5,
                            borderRadius: 2,
                            mt: 1,
                            fontSize: '0.75rem',
                            boxShadow: 2,
                            cursor: isClickable ? 'pointer' : 'default',
                            transition: 'all 0.3s',
                            border: isClickable ? '2px solid rgba(255,255,255,0.3)' : '2px solid transparent',
                            '&:hover': isClickable ? {
                              boxShadow: 4,
                              transform: 'translateY(-2px)',
                              border: '2px solid rgba(255,255,255,0.8)',
                            } : {},
                          }}
                        >
                          <Typography variant="body2" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem' }}>
                            {aula.licao || aula.numero_licao || `Aula ${aula.id}`}
                          </Typography>
                          {aula.turma_nome && (
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', opacity: 0.9 }}>
                              {aula.turma_nome}
                            </Typography>
                          )}
                          {aula.data && (
                            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', mt: 0.5, fontWeight: 'bold' }}>
                              {new Date(aula.data).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          )}
                          
                          {/* Etiquetas de Professor e Monitor */}
                          {(aula.professor_nome || aula.monitor_nome) && (
                            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {aula.professor_nome && (
                                <Chip
                                  label={`Prof: ${getPrimeiroNome(aula.professor_nome)}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    fontSize: '0.7rem',
                                    height: '22px',
                                    '& .MuiChip-label': { px: 1 }
                                  }}
                                />
                              )}
                              {aula.monitor_nome && (
                                <Chip
                                  label={`Monitor: ${getPrimeiroNome(aula.monitor_nome)}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    fontSize: '0.7rem',
                                    height: '22px',
                                    '& .MuiChip-label': { px: 1 }
                                  }}
                                />
                              )}
                            </Box>
                          )}
                          
                          {/* Indicador de ação */}
                          {isClickable && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: 'block', 
                                mt: 1, 
                                fontSize: '0.65rem', 
                                fontWeight: 'bold',
                                textAlign: 'center',
                                opacity: 0.9,
                                textTransform: 'uppercase'
                              }}
                            >
                              {podeReservar && <><CheckCircleIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />RESERVAR</>}
                              {podeReservarMonitor && <><CheckCircleIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />MONITORAR</>}
                              {podeLiberar && <><CancelIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />LIBERAR</>}
                              {podeLiberarMonitor && <><CancelIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />LIBERAR</>}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Paper>
                ) : (
                  <Box sx={{ minHeight: { xs: 60, sm: 100, md: 140 }, width: '100%' }} />
                )}
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ mt: 3 }}>
          <Button variant="outlined" onClick={() => navigate(-1)} fullWidth sx={{ maxWidth: { sm: 200 } }}>
            Voltar
          </Button>
        </Box>
      </Container>
  );
}

export default CalendarioAulas;
